import { useState } from 'react';
import Loading from '../elements/Loading';
import { ChevronDown, ChevronUp } from 'lucide-react';

const CustomerInfo = ({ customer }) => {
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    address: false,
    contact: false,
    emergency: false
  });

  if (!customer) {
    return <Loading />;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const SectionHeader = ({ title, section, isExpanded }) => (
    <div 
      className="flex items-center justify-between py-3 cursor-pointer" 
      onClick={() => toggleSection(section)}
    >
      <h2 className="text-base font-medium text-gray-900">{title}</h2>
      {isExpanded ? 
        <ChevronUp className="w-4 h-4 text-gray-500" /> : 
        <ChevronDown className="w-4 h-4 text-gray-500" />
      }
    </div>
  );

  const DetailItem = ({ label, value }) => (
    <div className="py-2">
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-gray-900">{value || '—'}</p>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto px-4">
      <div className="space-y-4 divide-y divide-gray-100">
        <div>
          <SectionHeader 
            title="Personal Details" 
            section="personal"
            isExpanded={expandedSections.personal}
          />
          
          {expandedSections.personal && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 pb-3 z-0">
              <DetailItem
                label="Full Name"
                value={`${customer.first_name} ${customer.last_name}`}
              />
              <DetailItem
                label="Gender"
                value={customer.gender}
              />
              <DetailItem
                label="Date of Birth"
                value={customer.dob}
              />
              <DetailItem
                label="ID Number"
                value={customer.id_number}
              />
            </div>
          )}
        </div>

        <div>
          <SectionHeader 
            title="Location" 
            section="address"
            isExpanded={expandedSections.address}
          />
          
          {expandedSections.address && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 pb-3">
              <DetailItem
                label="City"
                value={customer.city}
              />
              <DetailItem
                label="State/Province"
                value={customer.state}
              />
              <DetailItem
                label="Country"
                value={customer.country}
              />
              <DetailItem
                label="Nationality"
                value={customer.nationality}
              />
            </div>
          )}
        </div>

        {/* Contact Details */}
        <div>
          <SectionHeader 
            title="Contact Information" 
            section="contact"
            isExpanded={expandedSections.contact}
          />
          
          {expandedSections.contact && (
            <div className="grid grid-cols-1 gap-3 pb-3">
              <DetailItem
                label="Email Address"
                value={customer.email}
              />
              <DetailItem
                label="Phone Number"
                value={customer.phone_number}
              />
            </div>
          )}
        </div>

        {/* Emergency Contacts */}
        <div>
          <SectionHeader 
            title="Emergency Contacts" 
            section="emergency"
            isExpanded={expandedSections.emergency}
          />
          
          {expandedSections.emergency && (
            <div className="pb-3">
              {/* Primary Contact */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div className="w-0.5 h-3 bg-gray-900 mr-1.5"></div>
                  <span className="text-xs font-medium text-gray-900">Primary</span>
                </div>
                
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 pl-2">
                  <DetailItem
                    label="Name"
                    value={customer?.next_of_kin1_name}
                  />
                  <DetailItem
                    label="Phone"
                    value={customer?.next_of_kin1_phone}
                  />
                  <DetailItem
                    label="Email"
                    value={customer?.next_of_kin1_email}
                  />
                  <DetailItem
                    label="Relationship"
                    value={customer?.next_of_kin1_relationship}
                  />
                </div>
              </div>
              
              {/* Secondary Contact */}
              {customer?.next_of_kin2_name && (
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-0.5 h-3 bg-gray-400 mr-1.5"></div>
                    <span className="text-xs font-medium text-gray-700">Secondary</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 pl-2">
                    <DetailItem
                      label="Name"
                      value={customer?.next_of_kin2_name}
                    />
                    <DetailItem
                      label="Phone"
                      value={customer?.next_of_kin2_phone}
                    />
                    <DetailItem
                      label="Email"
                      value={customer?.next_of_kin2_email}
                    />
                    <DetailItem
                      label="Relationship"
                      value={customer?.next_of_kin2_relationship}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerInfo;